import React, { useEffect, useState } from 'react';
import { buildApiUrl } from '../config';
import { formatDisplayDate } from '../utils/formatDisplayDate';
import { openStoredInvoicePdf } from '../utils/openStoredInvoicePdf';
import { buildThermalReceiptDoc } from '../utils/thermalReceiptPdf';
import './Vat.css';

const money = (n) => {
  const v = parseFloat(n);
  if (!Number.isFinite(v)) return '0.00';
  return v.toFixed(2);
};

const Vat = () => {
  const [view, setView] = useState('invoices'); // invoices | expenses | payments
  const [invoices, setInvoices] = useState([]);
  const [invoiceTotals, setInvoiceTotals] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseTotals, setExpenseTotals] = useState(null);
  const [payments, setPayments] = useState([]);
  const [overall, setOverall] = useState(null);
  const [overallError, setOverallError] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadInvoices = async () => {
    if (!fromDate || !toDate) {
      alert('Please select From and To dates');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        buildApiUrl(
          `api/vat/invoices?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`
        )
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load VAT invoices');
      setInvoices(data.data || []);
      setInvoiceTotals(data.totals || null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load VAT invoices');
      setInvoices([]);
      setInvoiceTotals(null);
    } finally {
      setLoading(false);
    }
  };

  const loadExpenses = async () => {
    if (!fromDate || !toDate) {
      alert('Please select From and To dates');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        buildApiUrl(`api/vat/expenses?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`)
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load VAT expenses');
      setExpenses(data.data || []);
      setExpenseTotals(data.totals || null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load VAT expenses');
      setExpenses([]);
      setExpenseTotals(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    setError('');
    try {
      const payRes = await fetch(buildApiUrl('api/vat/payments'));
      const payText = await payRes.text();
      let data;
      try {
        data = JSON.parse(payText);
      } catch (_) {
        throw new Error('VAT payments API did not return JSON. Restart the server and try again.');
      }
      if (!data.success) throw new Error(data.error || 'Failed to load VAT payments');
      setPayments(data.data || []);

      try {
        const overallRes = await fetch(buildApiUrl('api/vat/overall-summary'));
        const overallText = await overallRes.text();
        const overallData = JSON.parse(overallText);
        if (overallData.success) {
          setOverall(overallData.data);
          setOverallError('');
        } else {
          setOverall(null);
          setOverallError(overallData.error || 'Failed to load till-date figures');
        }
      } catch (e) {
        setOverall(null);
        setOverallError(e.message || 'Failed to load till-date figures. Restart the server.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load VAT payments');
      setPayments([]);
      setOverall(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'payments') {
      loadPayments();
    } else {
      // invoices / expenses: wait for date range + Load button
      setInvoices([]);
      setInvoiceTotals(null);
      setExpenses([]);
      setExpenseTotals(null);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openVatDocument = async (row) => {
    try {
      if (row.source === 'thermal') {
        const receiptId = String(row.id || '').replace(/^th-/, '');
        const checkRes = await fetch(buildApiUrl(`api/thermal-receipts/check-data/${receiptId}`));
        const checkData = await checkRes.json();
        if (!checkData.success || !checkData.hasReceiptData) {
          alert(checkData.error || 'No stored thermal receipt data available to open.');
          return;
        }
        const res = await fetch(buildApiUrl(`api/thermal-receipts/get-data/${receiptId}`));
        const result = await res.json();
        if (!result.success) {
          alert(result.error || 'Failed to load thermal receipt');
          return;
        }
        const doc = buildThermalReceiptDoc(result.data);
        window.open(doc.output('bloburl'), '_blank');
        return;
      }

      const invoiceId = String(row.id || '').replace(/^inv-/, '');
      const checkRes = await fetch(buildApiUrl(`api/invoices/check-pdf-data/${invoiceId}`));
      const checkData = await checkRes.json();
      if (!checkData.success) {
        alert(checkData.error || 'Failed to check invoice data');
        return;
      }
      if (!checkData.hasPdfData) {
        alert('No stored PDF data for this invoice. It may have been created before PDF storage was added.');
        return;
      }
      const res = await fetch(buildApiUrl(`api/invoices/get-data/${invoiceId}`));
      const result = await res.json();
      if (!result.success) {
        alert(result.error || 'Failed to load invoice');
        return;
      }
      const { companyInfo, invoiceDetails, pdfSettings, selectedCompany } = result.data;
      openStoredInvoicePdf(companyInfo, invoiceDetails, pdfSettings, selectedCompany);
    } catch (err) {
      console.error('Error opening VAT document:', err);
      alert('Failed to open document. Please try again.');
    }
  };

  return (
    <div className="vat-container">
      <h1>VAT Module</h1>

      <div className="vat-radio-group">
        <label className={`vat-radio-label ${view === 'invoices' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="vatView"
            checked={view === 'invoices'}
            onChange={() => setView('invoices')}
          />
          VAT Collected (Invoices)
        </label>
        <label className={`vat-radio-label ${view === 'expenses' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="vatView"
            checked={view === 'expenses'}
            onChange={() => setView('expenses')}
          />
          VAT paid (expense)
        </label>
        <label className={`vat-radio-label ${view === 'payments' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="vatView"
            checked={view === 'payments'}
            onChange={() => setView('payments')}
          />
          VAT Payments
        </label>
      </div>

      {(view === 'invoices' || view === 'expenses') && (
        <div className="vat-filters">
          <div className="form-group">
            <label htmlFor="vat-from">From</label>
            <input
              id="vat-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="vat-to">To</label>
            <input
              id="vat-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="vat-load-btn"
            onClick={view === 'invoices' ? loadInvoices : loadExpenses}
          >
            {view === 'invoices' ? 'Load Invoices' : 'Load Expenses'}
          </button>
        </div>
      )}

      {loading && <div className="vat-loading">Loading...</div>}
      {error && <div className="vat-empty">{error}</div>}

      {!loading && !error && view === 'invoices' && (
        <div className="vat-table-wrap">
          <table className="vat-table">
            <thead>
              <tr>
                <th>SR#</th>
                <th>Date</th>
                {/* <th>Type</th> */}
                <th>Invoice #</th>
                {/* <th>Company / Customer</th> */}
                {/* <th>Period</th> */}
                <th className="num">Excl VAT</th>
                <th className="num">VAT</th>
                <th className="num">Incl VAT</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="vat-empty">
                    {fromDate && toDate
                      ? 'No invoices / thermal receipts in this range.'
                      : 'Select a date range and click Load Invoices.'}
                  </td>
                </tr>
              ) : (
                invoices.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>
                    <td>{formatDisplayDate(row.doc_date)}</td>
                    {/* <td>{row.sourceLabel || row.source}</td> */}
                    <td>
                      <button
                        type="button"
                        className="vat-doc-link"
                        onClick={() => openVatDocument(row)}
                        title="Open invoice"
                      >
                        {row.doc_number}
                      </button>
                    </td>
                    {/* <td>{row.party_name}</td> */}
                    {/* <td>
                      {row.periodFrom && row.periodTo
                        ? `${row.periodFrom} → ${row.periodTo}`
                        : '-'}
                    </td> */}
                    <td className="num">{money(row.amountExclVat)}</td>
                    <td className="num">{money(row.vatAmount)}</td>
                    <td className="num">{money(row.amountInclVat)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {invoiceTotals && invoices.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td className="num">{money(invoiceTotals.amountExclVat)}</td>
                  <td className="num">{money(invoiceTotals.vatAmount)}</td>
                  <td className="num">{money(invoiceTotals.amountInclVat)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {!loading && !error && view === 'expenses' && (
        <div className="vat-table-wrap">
          <table className="vat-table">
            <thead>
              <tr>
                <th>SR#</th>
                <th>Date</th>
                <th>Expenses Details</th>
                <th className="num">Amount Excl VAT</th>
                <th className="num">VAT</th>
                <th className="num">Amount Incl VAT</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="vat-empty">
                    {fromDate && toDate
                      ? 'No expenses in this range.'
                      : 'Select a date range and click Load Expenses.'}
                  </td>
                </tr>
              ) : (
                expenses.map((row, idx) => (
                  <tr key={row.ExpenseID}>
                    <td>{idx + 1}</td>
                    <td>{formatDisplayDate(row.displayDate || row.expenseDate)}</td>
                    <td>
                      {[row.expenseType, row.supplierName, row.description]
                        .filter((x) => x && String(x).trim())
                        .join(' | ') || '-'}
                    </td>
                    <td className="num">{money(row.amountExclVat)}</td>
                    <td className="num">{money(row.vatAmount)}</td>
                    <td className="num">{money(row.amountInclVat)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {expenseTotals && expenses.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td className="num">{money(expenseTotals.amountExclVat)}</td>
                  <td className="num">{money(expenseTotals.vatAmount)}</td>
                  <td className="num">{money(expenseTotals.amountInclVat)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {!loading && !error && view === 'payments' && (
        <>
          {/* Moved to Dashboard — VAT Till Date figures
          <div className="vat-overall-box">
            <h3>VAT Till Date</h3>
            {overallError && !overall ? (
              <p className="vat-empty" style={{ padding: 8 }}>{overallError}</p>
            ) : (
              <div className="vat-overall-grid">
                <div>
                  <span>Total VAT Collected</span>
                  <strong>AED {money(overall?.collectedVat)}</strong>
                </div>
                <div>
                  <span>Invoice VAT</span>
                  <strong>AED {money(overall?.invoiceVat)}</strong>
                </div>
                <div>
                  <span>Thermal VAT</span>
                  <strong>AED {money(overall?.thermalVat)}</strong>
                </div>
                <div>
                  <span>Expense VAT</span>
                  <strong>AED {money(overall?.expenseVat)}</strong>
                </div>
                <div>
                  <span>Already Paid</span>
                  <strong>AED {money(overall?.alreadyPaid)}</strong>
                </div>
                <div>
                  <span>Remaining Till Date</span>
                  <strong>
                    AED {money(overall?.remaining)}
                    {overall && overall.remaining < 0 ? ' (claim)' : ''}
                  </strong>
                </div>
              </div>
            )}
          </div>
          */}
          <div className="vat-table-wrap">
          <table className="vat-table">
            <thead>
              <tr>
                <th>SR#</th>
                <th>Payment Date</th>
                {/* <th>Period</th> */}
                {/* <th className="num">Invoice VAT</th> */}
                {/* <th className="num">Thermal VAT</th> */}
                {/* <th className="num">Expense VAT</th> */}
                <th className="num">Amount Paid</th>
                <th>Method</th>
                {/* <th>Payee</th> */}
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="vat-empty">
                    No VAT payments recorded yet. Record them from Payments → VAT.
                  </td>
                </tr>
              ) : (
                payments.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>
                    <td>{formatDisplayDate(row.payment_date)}</td>
                    {/* <td>
                      {row.period_from && row.period_to
                        ? `${row.period_from} → ${row.period_to}`
                        : row.invoice_number || '-'}
                    </td> */}
                    {/* <td className="num">{money(row.invoice_vat)}</td> */}
                    {/* <td className="num">{money(row.thermal_vat)}</td> */}
                    {/* <td className="num">{money(row.expense_vat)}</td> */}
                    <td className="num">{money(row.amount)}</td>
                    <td>{row.payment_method || '-'}</td>
                    {/* <td>{row.payee || 'Govt'}</td> */}
                    <td>{row.notes || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

export default Vat;
